/**
 * @voltxampere/web — Auto-save debounce store (Sprint 21 C3)
 *
 * Subscribes to `currentCircuit` and persists changes to IndexedDB
 * via `@voltxampere/persistence` after a debounce window (default
 * 5 s). Multiple rapid edits collapse to a single save: every new
 * mutation cancels the pending timer and schedules a fresh one.
 *
 * Boot wiring lives in `main.ts` — `bootAutoSave()` is called once
 * after `bootDcRunner` / `bootTransientRunner` / `bootACRunner`.
 *
 * Sprint 21 ships a sync-only flow: save fires from the browser
 * event loop, no server upload. Sprint 22+ may layer remote sync
 * on top of the same debounce.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect, signal } from "@preact/signals-core";
import { putCircuit } from "@voltxampere/persistence";
import type { Circuit } from "@voltxampere/schema";

import { currentCircuit } from "./circuit.js";

export const DEFAULT_AUTOSAVE_DEBOUNCE_MS = 5_000;

/**
 * Status surfaced for the topbar / future "Veri" tab status pill.
 *  - `idle`     — no pending save
 *  - `pending`  — debounce timer running, edit just landed
 *  - `saving`   — IDB write in flight
 *  - `saved`    — last save completed successfully (transient state,
 *                 collapses back to `idle` after 2 s)
 *  - `error`    — last save threw; message exposed via `autoSaveError`
 */
export type AutoSaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

export const autoSaveStatus = signal<AutoSaveStatus>("idle");
export const autoSaveError = signal<string | null>(null);
export const autoSaveLastSavedAt = signal<number | null>(null);

interface BootAutoSaveOpts {
  /** Override the debounce window. Tests use this with fakeTimers. */
  readonly debounceMs?: number;
  /**
   * Optional save adapter — wraps `putCircuit` so tests can inject
   * a fake without touching IndexedDB. Production callers leave it
   * unset; the default delegates to `putCircuit(circuit)`.
   */
  readonly save?: (circuit: Circuit) => Promise<void>;
}

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let savedTransitionTimer: ReturnType<typeof setTimeout> | null = null;
let activeUnsubscribe: (() => void) | null = null;

/**
 * Activate auto-save. Returns a teardown function for tests; in
 * production main.ts calls this once and leaves the subscription
 * alive for the lifetime of the page.
 */
export function bootAutoSave(opts: BootAutoSaveOpts = {}): () => void {
  if (activeUnsubscribe) {
    // Idempotent — re-binding without teardown would leak timers.
    return activeUnsubscribe;
  }
  const debounceMs = opts.debounceMs ?? DEFAULT_AUTOSAVE_DEBOUNCE_MS;
  const save = opts.save ?? defaultSave;

  // Skip the first effect run (initial subscribe) so a fresh boot
  // doesn't immediately re-save the fixture.
  let firstRun = true;
  const dispose = effect(() => {
    const circuit = currentCircuit.value;
    if (firstRun) {
      firstRun = false;
      return;
    }
    if (!circuit) return;
    schedule(circuit, debounceMs, save);
  });

  activeUnsubscribe = (): void => {
    dispose();
    if (pendingTimer !== null) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    if (savedTransitionTimer !== null) {
      clearTimeout(savedTransitionTimer);
      savedTransitionTimer = null;
    }
    autoSaveStatus.value = "idle";
    activeUnsubscribe = null;
    firstRun = true;
  };
  return activeUnsubscribe;
}

function schedule(
  circuit: Circuit,
  debounceMs: number,
  save: (circuit: Circuit) => Promise<void>,
): void {
  if (pendingTimer !== null) clearTimeout(pendingTimer);
  if (savedTransitionTimer !== null) {
    clearTimeout(savedTransitionTimer);
    savedTransitionTimer = null;
  }
  autoSaveStatus.value = "pending";
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    runSave(circuit, save);
  }, debounceMs);
}

function runSave(circuit: Circuit, save: (circuit: Circuit) => Promise<void>): void {
  autoSaveStatus.value = "saving";
  autoSaveError.value = null;
  if (import.meta.env.DEV) {
    console.info(
      `[VXA save] auto-save start · circuit=${circuit.name} components=${String(circuit.components.length)}`,
    );
  }
  save(circuit)
    .then(() => {
      autoSaveStatus.value = "saved";
      autoSaveLastSavedAt.value = Date.now();
      if (import.meta.env.DEV) {
        console.info(`[VXA save] auto-save done · circuit=${circuit.name}`);
      }
      // Drop back to idle after 2 s so the topbar status doesn't
      // permanently read "Saved".
      savedTransitionTimer = setTimeout(() => {
        if (autoSaveStatus.value === "saved") {
          autoSaveStatus.value = "idle";
        }
        savedTransitionTimer = null;
      }, 2_000);
    })
    .catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      autoSaveStatus.value = "error";
      autoSaveError.value = msg;
      if (import.meta.env.DEV) {
        console.error("[VXA save] auto-save failed:", err);
      }
    });
}

async function defaultSave(circuit: Circuit): Promise<void> {
  // Sprint 24 A1 (B1 R2 drift fix) — structuredClone before the
  // IDB write. structuredClone snapshots the circuit, severing
  // any module-level preset / fixture reference share that may
  // still be lingering after the edit-param immutable update.
  // Without the snapshot, IDB stored shape can be subtly polluted
  // by later mutations on the same component reference (the
  // root cause of B1: R2 reverted to mockup default after reload).
  await putCircuit(structuredClone(circuit));
}

/**
 * Test cleanup helper — fully tears down state. Distinct from the
 * production teardown returned by `bootAutoSave` because it also
 * resets the exposed signals so test assertions on a fresh boot
 * see the cold initial state.
 */
export function resetAutoSaveForTests(): void {
  activeUnsubscribe?.();
  activeUnsubscribe = null;
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  if (savedTransitionTimer !== null) {
    clearTimeout(savedTransitionTimer);
    savedTransitionTimer = null;
  }
  autoSaveStatus.value = "idle";
  autoSaveError.value = null;
  autoSaveLastSavedAt.value = null;
}
